import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";

export const fetchCurrentSubscription = createAsyncThunk(
  "billing/fetchCurrentSubscription",
  async (_, thunkApi) => {
    const res = await apiRequest("api/billing/subscription", { method: "GET" });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to fetch subscription."
      );
    }

    // API returns subscription info or `null` when none exist.
    return res.data ?? null;
  },
  {
    condition: (arg, { getState }) => {
      const state = getState();
      const billingState = state?.billing;
      const token = state?.auth?.token;

      if (!token) return false;
      if (arg?.force) return true;

      if (!billingState) return true;
      return billingState.status === "idle" || billingState.status === "failed";
    },
  }
);

export const cancelSubscription = createAsyncThunk(
  "billing/cancelSubscription",
  async (_, thunkApi) => {
    const res = await apiRequest("api/billing/cancel", { method: "POST" });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to cancel subscription."
      );
    }

    return res.data ?? { success: true };
  }
);

const billingSlice = createSlice({
  name: "billing",
  initialState: {
    status: "idle", // idle | loading | succeeded | failed
    error: "",
    subscription: null,
    cancelStatus: "idle", // idle | loading | succeeded | failed
    cancelError: "",
  },
  reducers: {
    clearSubscription(state) {
      state.status = "idle";
      state.error = "";
      state.subscription = null;
      state.cancelStatus = "idle";
      state.cancelError = "";
    },
    clearCancelState(state) {
      state.cancelStatus = "idle";
      state.cancelError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";
        state.subscription = action.payload ?? null;
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch subscription.";
        state.subscription = null;
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.cancelStatus = "loading";
        state.cancelError = "";
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.cancelStatus = "succeeded";
        state.cancelError = "";
        if (state.subscription) {
          state.subscription = {
            ...state.subscription,
            cancel_at_period_end: true,
            status: state.subscription.status || "active",
          };
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.cancelStatus = "failed";
        state.cancelError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to cancel subscription.";
      });
  },
});

export const { clearSubscription, clearCancelState } = billingSlice.actions;
export default billingSlice.reducer;
